import { useCallback, useEffect, useState } from 'react'

import { format } from 'date-fns'
import * as ScreenOrientation from 'expo-screen-orientation'
import {
  GetChartDataParams,
  useGetContractsAmountQuery,
  useGetCreditInsuranceQuery,
  useGetFpd10Query,
  useGetMarketShareQuery,
  useGetOpenedAccountQuantityQuery,
  useGetOver30M3Query,
  useGetPotentialQuery,
  useGetProductionQuery,
  useGetSantanderAutoInsuranceQuery,
  useGetSantanderAutoTypeQuery,
  useGetSpreadPercentageQuery,
  useGetWebmotorsQuery
} from 'integration/resources/panels'
import { useBreakpointValue, useTheme } from 'native-base'
import { PANELS_TYPES_ENUM, panelsFullChartsProps } from 'organisms/TabPanels/constants'
import { getShortMonthAndYear } from 'src/utils'
import { LogEventScreen } from 'src/utils/logEvents'

import { UseChartScreen } from './ChartScreen.types'

const changeScreenOrientation = (orientation: ScreenOrientation.OrientationLock) => {
  return ScreenOrientation.lockAsync(orientation)
}

export const useChartScreen: UseChartScreen = ({ navigation, route }) => {
  useEffect(() => {
    changeScreenOrientation(ScreenOrientation.OrientationLock.LANDSCAPE_RIGHT).then(() => {
      const rotateTimeout = setTimeout(() => {}, 1500)

      clearTimeout(rotateTimeout)
    })
  }, [])

  const isMobile = useBreakpointValue({ base: true, lg: false })

  const theme = useTheme()

  const tabNumber = route.params.tabNumber

  const type = route.params.type

  const [showModal, setShowModal] = useState(true)

  const timeoutModal = setTimeout(() => {
    setShowModal(false)
  }, 2500)

  useEffect(() => {
    return () => {
      clearTimeout(timeoutModal)
    }
  }, [timeoutModal])

  const [showSpinner, setShowSpinner] = useState(true)

  useEffect(() => {
    const date = new Date()

    const formated = format(date, 'dd-MM-yyyy HH:mm')

    LogEventScreen({
      screen_name: `Grafico_${panelsFullChartsProps[type].slug}`,
      screen_class: `Grafico_${panelsFullChartsProps[type].slug}`,
      date: formated,
      hora: format(date, 'HH'),
      minutos: format(date, 'mm')
    })
  }, [type])

  useEffect(() => {
    if (!showSpinner) return

    const timeoutSpinner = setTimeout(() => {
      setShowSpinner(!showSpinner)
    }, 3000)

    return () => {
      clearTimeout(timeoutSpinner)
    }
  }, [showSpinner])

  const handleGoBack = useCallback(() => {
    navigation.goBack()
  }, [navigation])

  useEffect(
    () =>
      navigation.addListener('beforeRemove', (e) => {
        if (e.data.action.type === 'GO_BACK' && isMobile) {
          // Prevent default behavior of leaving the screen
          e.preventDefault()

          setShowSpinner(true)

          changeScreenOrientation(ScreenOrientation.OrientationLock.PORTRAIT_UP)

          setTimeout(() => {
            navigation.dispatch(e.data.action)
          }, 3000)
        }
      }),
    [navigation, isMobile]
  )

  const switchHook = (type: PANELS_TYPES_ENUM) => {
    switch (type) {
      case `${PANELS_TYPES_ENUM.CONTRACTS_AMOUNT}GoodsAndServices`:
        return useGetContractsAmountQuery

      case PANELS_TYPES_ENUM.CONTRACTS_AMOUNT:
        return useGetContractsAmountQuery

      case PANELS_TYPES_ENUM.PRODUCTION:
        return useGetProductionQuery

      case PANELS_TYPES_ENUM.SPREAD:
        return useGetSpreadPercentageQuery

      case PANELS_TYPES_ENUM.POTENTIAL:
        return useGetPotentialQuery

      case PANELS_TYPES_ENUM.MARKETING_SHARE:
        return useGetMarketShareQuery

      case PANELS_TYPES_ENUM.MARKET_VISUALIZATION:
        return useGetMarketShareQuery

      case PANELS_TYPES_ENUM.CREDIT_LIFE_INSURANCE:
        return useGetCreditInsuranceQuery

      case PANELS_TYPES_ENUM.SANTANDER_AUTO_INSURANCE:
        return useGetSantanderAutoInsuranceQuery

      case PANELS_TYPES_ENUM.TYPES_SANTANDER_AUTO:
        return useGetSantanderAutoTypeQuery

      case PANELS_TYPES_ENUM.ACCOUNT_AMOUNT:
        return useGetOpenedAccountQuantityQuery

      case PANELS_TYPES_ENUM.WEBMOTORS:
        return useGetWebmotorsQuery

      case PANELS_TYPES_ENUM.FPD10:
        return useGetFpd10Query

      case PANELS_TYPES_ENUM.OVER30_M3:
        return useGetOver30M3Query

      default:
        return []
    }
  }

  const hook = switchHook(type)

  //@ts-ignore
  const data = hook({
    year_month_qty: 13,
    tab_number: tabNumber
  } as GetChartDataParams)

  const processedAt =
    type === PANELS_TYPES_ENUM.MARKET_VISUALIZATION
      ? data?.data?.data?.data?.market_visualization_processed_at
      : data?.data?.data?.data?.processed_at

  const isShortFormat = data?.data?.data?.data?.is_short_format ?? false

  const switchChartData = (type: PANELS_TYPES_ENUM) => {
    switch (type) {
      case `${PANELS_TYPES_ENUM.CONTRACTS_AMOUNT}GoodsAndServices`:
        return data?.data && data.data.data && data.data.data.data
          ? [
              data.data.data.data.contracts_quantity,
              data.data.data.data.insurance_contracts_quantity
            ]
          : []

      case PANELS_TYPES_ENUM.CONTRACTS_AMOUNT:
        return data?.data && data.data.data && data.data.data.data
          ? [
              data.data.data.data.contracts_quantity,
              data.data.data.data.insurance_contracts_quantity,
              data.data.data.data.insurance_auto_contracts_quantity
            ]
          : []

      case PANELS_TYPES_ENUM.PRODUCTION:
        return data?.data && data.data.data && data.data.data.data
          ? data.data.data.data.production
          : []

      case PANELS_TYPES_ENUM.SPREAD:
        return data?.data && data.data.data && data.data.data.data
          ? [data.data.data.data.net_spread, data.data.data.data.gross_spread]
          : []

      case PANELS_TYPES_ENUM.POTENTIAL:
        return data?.data && data.data.data && data.data.data.data
          ? data.data.data.data.potential
          : []

      case PANELS_TYPES_ENUM.MARKETING_SHARE:
        return data?.data && data.data.data && data.data.data.data
          ? data.data.data.data.actual_share
          : []

      case PANELS_TYPES_ENUM.MARKET_VISUALIZATION:
        return data?.data && data.data.data && data.data.data.data
          ? data.data.data.data.market_visualization
          : []

      case PANELS_TYPES_ENUM.CREDIT_LIFE_INSURANCE:
        return data?.data && data.data.data && data.data.data.data
          ? [data.data.data.data.ip_insurance_spf_qty, data.data.data.data.ip_insurance_spf_volume]
          : []

      case PANELS_TYPES_ENUM.SANTANDER_AUTO_INSURANCE:
        return data?.data && data.data.data && data.data.data.data
          ? data.data.data.data.ip_auto_insurance_qty
          : []

      case PANELS_TYPES_ENUM.TYPES_SANTANDER_AUTO:
        return data?.data && data.data.data && data.data.data.data
          ? [
              data.data.data.data.complete_insurance,
              data.data.data.data.fire_theft_insurance,
              data.data.data.data.third_party_insurance,
              data.data.data.data.loose_insurance
            ]
          : []

      case PANELS_TYPES_ENUM.ACCOUNT_AMOUNT:
        return data?.data && data.data.data && data.data.data.data
          ? data.data.data.data.accounts_opened_quantity
          : []

      case PANELS_TYPES_ENUM.WEBMOTORS:
        return data?.data && data.data.data && data.data.data.data
          ? data.data.data.data.web_realized_value
          : []

      case PANELS_TYPES_ENUM.FPD10:
        return data?.data && data.data.data && data.data.data.data
          ? data.data.data.data.fpd10_percentage
          : []

      case PANELS_TYPES_ENUM.OVER30_M3:
        return data?.data && data.data.data && data.data.data.data
          ? data.data.data.data.over30_m3_calculated
          : []

      default:
        return []
    }
  }

  const chartsData = switchChartData(type)

  const chartXValues = data?.data?.data?.data?.months.map((item: { year_month: number }) =>
    getShortMonthAndYear(String(item.year_month))
  )

  const switchChartDataColors = (type: PANELS_TYPES_ENUM) => {
    switch (type) {
      case `${PANELS_TYPES_ENUM.CONTRACTS_AMOUNT}GoodsAndServices`:
        return [theme.colors.primary[800], theme.colors.primary[500]]

      case PANELS_TYPES_ENUM.CONTRACTS_AMOUNT:
        return [theme.colors.primary[800], theme.colors.primary[500], theme.colors.primary[300]]

      case PANELS_TYPES_ENUM.SPREAD:
        return [theme.colors.primary[500], theme.colors.primary[300]]

      case PANELS_TYPES_ENUM.CREDIT_LIFE_INSURANCE:
        return [theme.colors.primary[500], theme.colors.primary[300]]

      case PANELS_TYPES_ENUM.TYPES_SANTANDER_AUTO:
        return [
          theme.colors.primary[800],
          theme.colors.primary[500],
          theme.colors.primary[300],
          theme.colors.gray[200]
        ]

      default:
        return []
    }
  }

  return {
    isMobile,
    handleGoBack,
    showModal,
    setShowModal,
    showSpinner,
    setShowSpinner,
    type,
    chartsData,
    chartsDataIsLoading: data.isLoading,
    chartsDataColors: switchChartDataColors(type),
    chartXValues,
    tabNumber,
    processedAt,
    isShortFormat
  }
}
